import client from '../client';
import type { ApiResponse } from '../../types/api';

/**
 * Platform referral program (account-scoped, not store-scoped).
 *
 * A referral code belongs to the person, not to one of their stores, so these
 * endpoints sit at /dashboard/referrals with no storeId segment.
 */

export interface ReferralProgramTerms {
  active: boolean;
  currency: string;
  firstSaleReward: number;
  gmvThreshold: number;
  gmvReward: number;
  windowDays: number;
  cookieDays: number;
  minPayout: number;
  requireVerifiedMerchant: boolean;
}

export interface ReferralFunnel {
  clicks: number;
  signups: number;
  verified: number;
  firstSales: number;
  qualified: number;
}

export interface ReferralMe {
  code: string;
  link: string;
  status: 'active' | 'suspended';
  currency: string;
  counters: Partial<ReferralFunnel>;
  totals: { earned?: number; pending?: number; reversed?: number };
  balance: number;
  program: ReferralProgramTerms;
}

export interface ReferralStats {
  days: number;
  traffic: {
    visits: number;
    uniqueVisitors: number;
    sessions: number;
    series: Array<{ date: string; visits: number; uniqueVisitors: number }>;
    countries: Array<{ country: string; visits: number; uniqueVisitors: number }>;
    devices: Array<{ device: string; visits: number }>;
    sources: Array<{ source: string; visits: number }>;
  };
  funnel: ReferralFunnel;
}

export interface ReferralAwardView {
  amount: number;
  at: string | null;
  state: 'pending' | 'approved' | 'rejected' | 'reversed' | null;
}

export interface ReferralItem {
  id: string;
  merchant: string;
  email: string | null;
  signupAt: string;
  verifiedAt: string | null;
  firstSaleAt: string | null;
  windowEndsAt: string;
  windowOpen: boolean;
  gmv: number;
  gmvThreshold: number;
  qualifyingOrders: number;
  stores: Array<{ slug: string | null; name: string | null; attachedAt: string }>;
  awards: { firstSale: ReferralAwardView | null; gmv: ReferralAwardView | null };
  status: string;
}

export interface ReferralEarnings {
  balance: number;
  currency: string;
  totals: { earned?: number; pending?: number; reversed?: number };
  rewards: Array<{
    id: string;
    rule: string;
    description: { ar: string; en: string } | null;
    amount: number;
    currency: string;
    state: 'pending' | 'approved' | 'rejected' | 'reversed';
    storeName: string | null;
    createdAt: string;
    approvedAt: string | null;
  }>;
  ledger: Array<{
    id: string;
    kind: 'credit' | 'debit';
    description: string | null;
    descriptionEn: string | null;
    amount: number;
    currency: string;
    balanceAfter: number;
    storeName: string | null;
    createdAt: string;
  }>;
}

export const referralsApi = {
  me: async (): Promise<ReferralMe> => {
    const res = await client.get<ApiResponse<ReferralMe>>('/dashboard/referrals/me');
    return res.data.data!;
  },

  stats: async (days = 30): Promise<ReferralStats> => {
    const res = await client.get<ApiResponse<ReferralStats>>('/dashboard/referrals/stats', { params: { days } });
    return res.data.data!;
  },

  list: async (page = 1, limit = 20) => {
    const res = await client.get<ApiResponse<{
      referrals: ReferralItem[];
      pagination: { page: number; limit: number; total: number; pages: number };
    }>>('/dashboard/referrals/list', { params: { page, limit } });
    return res.data.data!;
  },

  earnings: async (): Promise<ReferralEarnings> => {
    const res = await client.get<ApiResponse<ReferralEarnings>>('/dashboard/referrals/earnings');
    return res.data.data!;
  },
};
